// File: src/EnglishTool/AdvanceEnglishHelper.js

import { shuffleArray } from "./shuffleArray.js";

/** ================= LITERARY DEVICES ================= **/

export function buildLiteraryDeviceMC(pool) {
  // pool: [{ line, device, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.device, ...(item.distractors || [])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `Which literary device is used in this line?\n"${item.line}"`,
    correctAnswer: item.device,
    options,
  };
}

/** ============== SENTENCE TYPE/STRUCTURE ============= **/

export function buildSentenceTypeMC(pool) {
  // pool: [{ sentence, type, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.type, ...(item.distractors || ["Simple", "Compound", "Complex", "Compound-Complex"])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `What type of sentence is this?\n"${item.sentence}"`,
    correctAnswer: item.type,
    options,
  };
}

/** ================== TEXT STRUCTURE ================== **/

export function buildTextStructureMC(pool) {
  // pool: [{ passage, structure, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.structure, ...(item.distractors || ["Cause/Effect", "Problem/Solution", "Compare/Contrast", "Chronological", "Description"])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `What is the text structure of this passage?\n"${item.passage}"`,
    correctAnswer: item.structure,
    options,
  };
}

/** ================= CONNOTATION/DENOTATION ============== **/

export function buildConnotationMC(pool) {
  // pool: [{ word, connotation, options: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.connotation, ...(item.options || ["Positive", "Negative", "Neutral"])]).slice(0, 3);
  return {
    questionType: "MC",
    question: `What is the connotation of the word "${item.word}"?`,
    correctAnswer: item.connotation,
    options,
  };
}

/** ==================== MEDIA LITERACY ==================== **/

export function buildMediaDeviceMC(pool) {
  // pool: [{ mediaText, device, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.device, ...(item.distractors || ["Bandwagon", "Testimonial", "Emotional Appeal", "Repetition"])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `Which persuasive device or technique is used in this media text?\n"${item.mediaText}"`,
    correctAnswer: item.device,
    options,
  };
}

/** ============== POETIC DEVICES & POETRY FORMS ============== **/

export function buildPoeticDeviceMC(pool) {
  // pool: [{ line, device, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.device, ...(item.distractors || ["Metaphor", "Simile", "Alliteration", "Personification", "Onomatopoeia"])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `Which poetic device is used in this line?\n"${item.line}"`,
    correctAnswer: item.device,
    options,
  };
}

export function buildIdentifyPoeticDeviceQ(pool) {
  // pool: [{ stanza, devices: [device1, device2, ...] }]
  const item = shuffleArray(pool)[0];
  return {
    questionType: "IDENTIFY",
    question: `Identify all poetic devices in this stanza:\n"${item.stanza}"\n(Type your answers, separated by commas)`,
    correctAnswers: item.devices,
  };
}

export function buildRhymeSchemeMC(pool) {
  // pool: [{ poem, scheme, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.scheme, ...(item.distractors || ["ABAB", "AABB", "ABCABC", "Free Verse"])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `What is the rhyme scheme of this poem?\n"${item.poem}"`,
    correctAnswer: item.scheme,
    options,
  };
}

export function buildPoetryFormMC(pool) {
  // pool: [{ poem, form, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.form, ...(item.distractors || ["Sonnet", "Haiku", "Free Verse", "Limerick", "Ballad"])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `What is the form of this poem?\n"${item.poem}"`,
    correctAnswer: item.form,
    options,
  };
}

export function buildLineStanzaMC(pool) {
  // pool: [{ form, lines, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([String(item.lines), ...(item.distractors || ["3", "4", "5", "6", "8", "14"])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `How many lines are in a ${item.form}?`,
    correctAnswer: String(item.lines),
    options,
  };
}

/** ==================== SHAKESPEARE/DRAMA =================== **/

export function buildShakespeareVocabMC(pool) {
  // pool: [{ archaic, modern, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.modern, ...(item.distractors || [])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `What does the Shakespearean word "${item.archaic}" mean in modern English?`,
    correctAnswer: item.modern,
    options,
  };
}

export function buildIambicPentameterMC(pool) {
  // pool: [{ line, isIambic, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.line, ...(item.distractors || [])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `Which line below is written in iambic pentameter?`,
    correctAnswer: item.line,
    options,
  };
}

export function buildDramaticDeviceMC(pool) {
  // pool: [{ passage, device, distractors: [] }]
  const item = shuffleArray(pool)[0];
  const options = shuffleArray([item.device, ...(item.distractors || ["Soliloquy", "Monologue", "Aside", "Stage Direction"])]).slice(0, 4);
  return {
    questionType: "MC",
    question: `What dramatic device is used in this passage?\n"${item.passage}"`,
    correctAnswer: item.device,
    options,
  };
}

/** ==================== LITERARY ANALYSIS =================== **/

export function buildSpeakerMoodMC(pool) {
    if (!pool || !pool.length) return null;
    const item = shuffleArray(pool)[0];
    return {
      questionType: "MC_MULTI",
      question: `Read this excerpt:\n"${item.excerpt}"\nWho is the speaker? What is the mood?`,
      options: {
        speaker: shuffleArray([item.speaker, ...item.speakerDistractors]),
        mood: shuffleArray([item.mood, ...item.moodDistractors])
      },
      correctAnswer: { speaker: item.speaker, mood: item.mood }
    };
  }
